(() => {
  'use strict';

  // These pure helpers are shared by the browser and the Node regression checks.
  function resolveDevice(product, value) {
    const devices = product.devices || [];
    if (devices.includes(value)) return value;
    if (/^iPhone Pro(?: Max)?$/.test(String(value))) {
      const suffix = String(value).replace('iPhone ', '');
      return devices.find(option => new RegExp(`^iPhone(?: \\d+)? ${suffix}$`).test(option)) || '';
    }
    return '';
  }

  function makeSanitizer(products) {
    return value => {
      if (!Array.isArray(value)) return [];
      const clean = [];
      for (const item of value.slice(0, 100)) {
        if (!item || typeof item !== 'object') continue;
        const product = products.find(product => product.id === item.id);
        if (!product) continue;
        const colors = product.colors || [];
        const devices = product.devices || [];
        const device = resolveDevice(product, item.device);
        const color = Number(item.color);
        const quantity = Math.floor(Number(item.qty));
        if (!Number.isInteger(color) || !colors[color] || !devices.includes(device) || !Number.isFinite(quantity) || quantity < 1) continue;
        const duplicate = clean.find(entry => entry.id === item.id && entry.color === color && entry.device === device);
        if (duplicate) duplicate.qty = Math.min(99, duplicate.qty + quantity);
        else clean.push({ id: item.id, color, device, qty: Math.min(99, quantity) });
      }
      return clean;
    };
  }

  function createSelectionStore(options) {
    let memory = null;
    let storageOK = false;
    const sanitize = options.sanitize;
    const parse = raw => {
      try {
        const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (Array.isArray(value)) return { version: 2, revision: 0, items: sanitize(value) };
        if (!value || typeof value !== 'object' || !Array.isArray(value.items)) return null;
        return { version: 2, revision: Number.isSafeInteger(value.revision) && value.revision >= 0 ? value.revision : 0, items: sanitize(value.items) };
      } catch (_) { return null; }
    };
    const safeRead = reader => { try { return parse(reader?.()); } catch (_) { return null; } };
    const persist = record => {
      try { options.write(record); storageOK = true; } catch (_) { storageOK = false; }
      try { options.writeFallback(record); } catch (_) { /* Memory and URL carry state in constrained local viewers. */ }
    };
    function read() {
      const stored = safeRead(options.read);
      const carried = safeRead(options.readFallback);
      const candidates = [stored, carried, memory].filter(Boolean);
      // A URL snapshot is only an initial seed. It never replaces a known state,
      // including a deliberately empty bag or a document restored from history.
      let latest = candidates.reduce((current, item) => !current || item.revision > current.revision ? item : current, null);
      if (!latest) latest = parse(options.seed) || { version: 2, revision: 0, items: [] };
      memory = { ...latest, items: sanitize(latest.items) };
      persist(memory);
      return { ...memory, items: memory.items.map(item => ({ ...item })) };
    }
    function mutate(update) {
      const latest = read();
      const items = sanitize(update(latest.items));
      memory = { version: 2, revision: Math.max(Number(options.now?.() || Date.now()), latest.revision + 1), items };
      persist(memory);
      return { ...memory, items: items.map(item => ({ ...item })) };
    }
    return { read, mutate, get storageOK() { return storageOK; } };
  }

  function searchMatches(products, query, selectedColors = new Map()) {
    const normalized = String(query || '').trim().toLocaleLowerCase();
    const tokens = normalized.split(/\s+/).filter(Boolean);
    return products.flatMap(product => {
      const colors = product.colors || [];
      const devices = product.devices || [];
      const series = product.category === 'duo' ? 'iPhone Duo fold folding' : 'iPhone 18 Series';
      const haystack = [product.name, product.shortName, series, product.tagline, product.description, ...devices, ...colors.map(color => color.name), ...(product.features || []).flatMap(feature => [feature.title, feature.body])].join(' ').toLocaleLowerCase();
      if (!tokens.every(token => haystack.includes(token))) return [];
      const score = label => {
        const words = String(label).toLocaleLowerCase().split(/\s+/);
        const meaningful = words.filter(word => !['iphone', '18'].includes(word));
        const hits = meaningful.filter(word => tokens.includes(word)).length;
        return hits ? hits * 10 + (meaningful.every(word => tokens.includes(word)) ? 2 : 0) - Math.max(0, meaningful.length - hits) : 0;
      };
      const bestColor = colors.reduce((best, color, index) => score(color.name) > best.score ? { index, score: score(color.name) } : best, { index: selectedColors.get(product.id) || 0, score: 0 });
      const bestDevice = devices.reduce((best, device) => score(device) > best.score ? { device, score: score(device) } : best, { device: '', score: 0 });
      return [{ product, color: bestColor.index, colorMatched: bestColor.score > 0, device: bestDevice.device }];
    });
  }

  if (typeof module === 'object' && module.exports && typeof document === 'undefined') {
    module.exports = { createSelectionStore, makeSanitizer, searchMatches, resolveDevice };
    return;
  }

  function init() {
    const catalog = window.XIKAVY || {};
    const products = Array.isArray(catalog.products) ? catalog.products : [];
    const assets = catalog.assets || {};
    const base = document.body.dataset.base || '';
    const page = document.body.dataset.page || '';
    const $ = (selector, root = document) => root.querySelector(selector);
    const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
    const escape = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
    const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value) || 0);
    const cents = value => Math.round(Number(value) * 100);
    const productById = id => products.find(product => product.id === id);
    const colorsFor = product => product.colors?.length ? product.colors : [{ name: 'Black', image: product.image, gallery: [product.image] }];
    const devicesFor = product => product.devices?.length ? product.devices : ['iPhone Pro'];
    const seriesName = product => product.category === 'duo' ? 'iPhone Duo' : 'iPhone 18 Series';
    const clampQuantity = quantity => Math.min(99, Math.max(1, Math.floor(Number(quantity) || 1)));
    const arrow = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4 12h16m-6-6 6 6-6 6"/></svg>';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const scrollBehavior = () => reducedMotion.matches ? 'auto' : 'smooth';
    const currentColors = new Map();
    const params = new URLSearchParams(window.location.search);
    let toastTimer;

    // Fit the visible bounds with CSS only. Every image retains the original file.
    function art(path, alt = '', loading = 'lazy') {
      const key = String(path || '').match(/(?:^|\/)(?:x-)?(a\d+)(?:\.|$)/)?.[1];
      const asset = key ? assets[key] : null;
      const source = asset?.src || path || '';
      const src = base + source;
      const width = Number(asset?.width);
      const height = Number(asset?.height);
      const box = asset?.bbox;
      const validBox = Array.isArray(box) && box.length === 4 && box.every(Number.isFinite) && box[2] > box[0] && box[3] > box[1] && width > 0 && height > 0;
      const transparent = asset?.hasTransparency === true || (asset?.hasTransparency !== false && validBox && (box[0] > 0 || box[1] > 0 || box[2] < width || box[3] < height));
      if (!validBox || !transparent) return `<img class="full-photo" src="${escape(src)}" alt="${escape(alt)}" loading="${loading}" decoding="async"${width > 0 && height > 0 ? ` width="${width}" height="${height}"` : ''}>`;
      const [left, top, right, bottom] = box;
      const boxWidth = right - left;
      const boxHeight = bottom - top;
      const percent = number => (number * 100).toFixed(5).replace(/\.?0+$/, '');
      return `<span class="original-art" style="--ratio:${boxWidth / boxHeight};aspect-ratio:${boxWidth}/${boxHeight}"><img src="${escape(src)}" alt="${escape(alt)}" loading="${loading}" decoding="async" width="${width}" height="${height}" style="width:${percent(width / boxWidth)}%;height:${percent(height / boxHeight)}%;left:${percent(-left / boxWidth)}%;top:${percent(-top / boxHeight)}%"></span>`;
    }

    function productUrl(product, color = 0, device = '') {
      const query = new URLSearchParams();
      if (Number.isInteger(color) && color > 0 && colorsFor(product)[color]) query.set('color', String(color));
      const resolvedDevice = resolveDevice(product, device);
      if (resolvedDevice) query.set('device', resolvedDevice);
      const destination = new URL(product.shopifyUrl, document.baseURI);
      query.forEach((value, key) => destination.searchParams.set(key, value));
      return destination.pathname + destination.search;
    }

    let toastReturnFocus = null;
    function hideToast(restoreFocus = true) {
      const node = $('#toast');
      if (!node) return;
      const ownedFocus = node.contains(document.activeElement);
      clearTimeout(toastTimer);
      node.classList.remove('is-visible');
      node.inert = true;
      node.hidden = true;
      node.setAttribute('aria-hidden', 'true');
      node.replaceChildren();
      if (restoreFocus && ownedFocus) {
        const destination = toastReturnFocus?.isConnected ? toastReturnFocus : $('#add-to-bag') || $('#bag-export');
        destination?.focus({ preventScroll: true });
      }
    }
    function toast(message, withBagLink = false) {
      const node = $('#toast');
      if (!node) return;
      if (!node.contains(document.activeElement)) toastReturnFocus = document.activeElement;
      node.innerHTML = `<span>${escape(message)}</span>${withBagLink ? `<a href="${escape(selectionHref('/cart'))}">View bag ${arrow}</a>` : ''}`;
      node.hidden = false;
      node.inert = false;
      node.removeAttribute('aria-hidden');
      node.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => hideToast(), withBagLink ? 6000 : 3500);
    }
    hideToast(false);

    const storageKey = 'xikavy-multipage-selection-v1';
    const stateKey = storageKey + ':state-v2';
    const validateBag = makeSanitizer(products);
    const rootScope = new URL('/', document.baseURI).href;
    const carrierPrefix = 'XIKAVY_SELECTION_V2:';
    let seed = null;
    if (params.get('bagempty') === '1') seed = { items: [], revision: 0 };
    else if (params.has('selection')) {
      try { seed = { items: validateBag(JSON.parse(params.get('selection'))), revision: 0 }; } catch (_) { /* Invalid imported state is ignored. */ }
    }
    function readCarrier() {
      const records = [];
      try {
        const value = sessionStorage.getItem(stateKey + ':' + rootScope);
        if (value) records.push(JSON.parse(value));
      } catch (_) { /* Session storage may be unavailable for local files. */ }
      try {
        if (window.name.startsWith(carrierPrefix)) {
          const envelope = JSON.parse(window.name.slice(carrierPrefix.length));
          if (envelope.scope === rootScope) records.push(envelope.state);
        }
      } catch (_) { /* The browsing context remains usable without a carrier. */ }
      return records.filter(Boolean).sort((a, b) => (b.revision || 0) - (a.revision || 0))[0] || null;
    }
    function writeCarrier(state) {
      try { sessionStorage.setItem(stateKey + ':' + rootScope, JSON.stringify(state)); } catch (_) { /* window.name carries the same local-only selection. */ }
      // window.name survives same-tab file navigation when localStorage is scoped
      // per file. Only our own namespaced value is replaced.
      try { if (!window.name || window.name.startsWith(carrierPrefix)) window.name = carrierPrefix + JSON.stringify({ scope: rootScope, state }); } catch (_) { /* URL seed remains available for first arrival. */ }
    }
    const selectionStore = createSelectionStore({
      read: () => localStorage.getItem(stateKey) || localStorage.getItem(storageKey),
      write: state => {
        localStorage.setItem(stateKey, JSON.stringify(state));
        localStorage.setItem(storageKey, JSON.stringify(state.items));
      },
      readFallback: readCarrier,
      writeFallback: writeCarrier,
      seed,
      sanitize: validateBag,
      now: () => Date.now()
    });
    let bag = selectionStore.read().items;
    let explicitlyEmpty = bag.length === 0;
    function readLatestBag() {
      bag = selectionStore.read().items;
      explicitlyEmpty = bag.length === 0;
      return bag;
    }
    function syncBag(refresh = true) {
      if (window.XIKAVY_SHOPIFY) return;
      const before = JSON.stringify(bag);
      readLatestBag();
      updateBagCount();
      if (refresh && before !== JSON.stringify(bag)) renderBag();
      syncSearchFormState();
    }

    function selectionHref(href) {
      if (window.XIKAVY_SHOPIFY) return href;
      const hashAt = href.indexOf('#');
      const hash = hashAt >= 0 ? href.slice(hashAt) : '';
      const beforeHash = hashAt >= 0 ? href.slice(0, hashAt) : href;
      const queryAt = beforeHash.indexOf('?');
      const path = queryAt >= 0 ? beforeHash.slice(0, queryAt) : beforeHash;
      const query = new URLSearchParams(queryAt >= 0 ? beforeHash.slice(queryAt + 1) : '');
      if (window.location.protocol !== 'file:' && selectionStore.storageOK) {
        query.delete('selection');
        query.delete('bagempty');
      } else if (bag.length) {
        query.set('selection', JSON.stringify(bag));
        query.delete('bagempty');
      } else {
        query.delete('selection');
        if (explicitlyEmpty) query.set('bagempty', '1');
        else query.delete('bagempty');
      }
      return path + (query.size ? `?${query}` : '') + hash;
    }

    function eligibleLink(anchor) {
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('//') || /^[a-z][a-z\d+.-]*:/i.test(href) || anchor.hasAttribute('download')) return false;
      try {
        const destination = new URL(href, document.baseURI);
        const root = new URL('/', document.baseURI);
        return destination.protocol === root.protocol && destination.host === root.host && destination.pathname.startsWith(root.pathname) ;
      } catch (_) { return false; }
    }
    function stampLink(anchor) {
      if (window.XIKAVY_SHOPIFY) return;
      if (eligibleLink(anchor)) anchor.setAttribute('href', selectionHref(anchor.getAttribute('href')));
    }
    function stampLinks() { $$('a[href]').forEach(stampLink); }
    // Normal links remain normal links, including Ctrl/Command-click and Back.
    document.addEventListener('click', event => {
      const anchor = event.target.closest?.('a[href]');
      if (anchor) { syncBag(false); stampLink(anchor); }
    }, { capture: true });
    document.addEventListener('auxclick', event => {
      const anchor = event.target.closest?.('a[href]');
      if (anchor) { syncBag(false); stampLink(anchor); }
    }, { capture: true });

    function updateBagCount() {
      if (window.XIKAVY_SHOPIFY) return;
      const count = bag.reduce((total, item) => total + item.qty, 0);
      $$('#bag-count,[data-bag-count]').forEach(node => {
        node.textContent = String(count);
        node.hidden = count === 0;
      });
      $$('[data-bag-link],#bag-open').forEach(node => node.setAttribute('aria-label', `Shopping bag, ${count} ${count === 1 ? 'item' : 'items'}`));
    }

    function bagTotal() { return bag.reduce((sum, item) => sum + cents(productById(item.id).price) * item.qty, 0); }
    function renderBag() {
      if (window.XIKAVY_SHOPIFY) return;
      const container = $('#bag-items');
      if (!container) return;
      container.innerHTML = bag.map((item, index) => {
        const product = productById(item.id);
        const color = colorsFor(product)[item.color];
        const href = selectionHref(productUrl(product, item.color, item.device));
        return `<article class="bag-item">
          <a class="bag-item-art product-art art-fit" href="${escape(href)}" aria-label="View ${escape(product.name)}">${art(color.image || product.image, `${product.name} in ${color.name}`)}</a>
          <div class="bag-item-info"><a class="bag-item-name" href="${escape(href)}">${escape(product.name)}</a><p>${escape(color.name)} · ${escape(item.device)}</p><p class="bag-item-price">${money(product.price)}</p>
          <div class="bag-item-controls"><div class="quantity-controls" role="group" aria-label="Quantity for ${escape(product.name)}"><button type="button" data-bag-qty="${index}" data-delta="-1" aria-label="Decrease quantity of ${escape(product.name)}"${item.qty <= 1 ? ' disabled' : ''}>−</button><output aria-label="Quantity">${item.qty}</output><button type="button" data-bag-qty="${index}" data-delta="1" aria-label="Increase quantity of ${escape(product.name)}"${item.qty >= 99 ? ' disabled' : ''}>+</button></div><button type="button" class="remove-item" data-bag-remove="${index}" aria-label="Remove ${escape(product.name)}">Remove</button></div></div><strong class="bag-line-total">${money(cents(product.price) * item.qty / 100)}</strong></article>`;
      }).join('');
      const empty = $('#bag-empty');
      if (empty) empty.hidden = bag.length > 0;
      const summary = $('#bag-summary');
      if (summary) summary.innerHTML = `<div class="bag-subtotal"><span>Subtotal</span><strong>${money(bagTotal() / 100)}</strong></div><p class="bag-preview-note">Preview selection. Orders and payments are not enabled.</p>`;
      if ($('#bag-export')) $('#bag-export').disabled = bag.length === 0;
    }
    function saveBag(update) {
      bag = selectionStore.mutate(update).items;
      explicitlyEmpty = bag.length === 0;
      updateBagCount();
      renderBag();
      stampLinks();
      try { history.replaceState(null, '', selectionHref(window.location.pathname + window.location.search + window.location.hash)); } catch (_) { /* Some file viewers disallow history updates. */ }
      syncSearchFormState();
    }
    window.addEventListener('pageshow', () => { syncBag(); stampLinks(); });
    window.addEventListener('focus', () => { syncBag(); stampLinks(); });
    window.addEventListener('storage', event => { if (!event.key || [storageKey, stateKey].includes(event.key)) { syncBag(); stampLinks(); } });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) { syncBag(); stampLinks(); } });
    function exportBag() {
      syncBag();
      if (!bag.length) return;
      const rows = bag.map(item => {
        const product = productById(item.id);
        return `<tr><td>${escape(product.name)}</td><td>${escape(colorsFor(product)[item.color].name)}</td><td>${escape(item.device)}</td><td>${item.qty}</td><td>${money(product.price)}</td><td>${money(cents(product.price) * item.qty / 100)}</td></tr>`;
      }).join('');
      const html = `<!doctype html><html lang="en"><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>XIKAVY — Saved selection</title><style>body{margin:0 auto;padding:48px 32px;max-width:1100px;font:15px/1.5 Arial,Helvetica,sans-serif;color:#111}h1{font-size:34px;line-height:1.15;margin:0 0 16px}p{color:#555}table{border-collapse:collapse;width:100%;margin:32px 0}th,td{text-align:left;vertical-align:top;padding:16px 10px;border-bottom:1px solid #ddd}th{font-size:12px;text-transform:uppercase;letter-spacing:.04em}td:nth-child(n+4){white-space:nowrap}tfoot td{font-weight:600;font-size:18px}button{border:0;background:#111;color:#fff;border-radius:32px;padding:12px 24px;font:inherit;cursor:pointer}.notice{border:1px solid #ccc;padding:16px;margin:24px 0}@media print{body{padding:0;font-size:11px}button{display:none}tr{break-inside:avoid}h1{font-size:24px}}@media(max-width:700px){body{padding:24px 16px}table{font-size:11px}th,td{padding:10px 5px}}</style><h1>XIKAVY<br>Saved selection.</h1><p>Saved ${escape(new Date().toLocaleString('en-US'))} · USD</p><div class="notice"><strong>This is a product selection, not an order.</strong><br>No order has been placed. No payment has been taken. Orders and payments are not enabled in this preview.</div><table><thead><tr><th>Product</th><th>Color</th><th>Device</th><th>Qty</th><th>Unit price</th><th>Subtotal</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><td colspan="5">Selection subtotal</td><td>${money(bagTotal() / 100)}</td></tr></tfoot></table><button type="button" onclick="window.print()">Print selection</button><p>Keep this file as a readable record of your chosen products, colors, devices and quantities.</p></html>`;
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'XIKAVY-saved-selection.html';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast('Your readable selection has been saved.');
    }
    $('#bag-export')?.addEventListener('click', exportBag);

    function updateCard(product, colorIndex) {
      const color = colorsFor(product)[colorIndex];
      if (!color) return;
      currentColors.set(product.id, colorIndex);
      $$('article[data-card]').filter(card => card.dataset.card === product.id).forEach(card => {
        const slot = $('.card-art', card);
        if (slot) slot.innerHTML = art(color.image || product.image, `${product.name} in ${color.name}`); const label = $('.card-color-name', card); if (label) label.textContent = color.name;
        $$('[data-card-color]', card).forEach(button => {
          const selected = Number(button.dataset.cardColor) === colorIndex;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
        $$('a[data-product-link]', card).forEach(anchor => {
          anchor.setAttribute('href', selectionHref(productUrl(product, colorIndex)));
        });
      });
    }

    const product = productById(document.body.dataset.product);
    let selectedColor = 0;
    let selectedDevice = product ? devicesFor(product)[0] : '';
    let photoIndex = 0;
    const galleryStage = $('.pdp-stage-art');
    const zoomDialog = $('#image-zoom');
    let zoomTrigger = null;
    function galleryPaths() {
      if (!product) return [];
      const color = colorsFor(product)[selectedColor];
      return [color.image || product.image, ...(color.gallery || [])].filter((path, index, list) => path && list.indexOf(path) === index);
    }
    function galleryCaption() {
      if (!product) return '';
      const color = colorsFor(product)[selectedColor];
      const path = galleryPaths()[photoIndex];
      const index = (color.gallery || []).indexOf(path);
      return index >= 0 && color.galleryCaptions?.[index]
        ? color.galleryCaptions[index]
        : photoIndex === 0 ? `${color.name} — product view.` : 'Shared product detail. Construction is shown for reference; the pictured color may differ from your selection.';
    }
    let indicatorDetail = null;
    let zoomStage = null;
    const zoomImage = $('#zoom-image');
    if (zoomImage && zoomDialog) {
      zoomStage = document.createElement('div');
      zoomStage.className = 'zoom-image-stage';
      zoomImage.before(zoomStage);
      zoomStage.appendChild(zoomImage);
    }
    function applyIndicatorZoom() {
      if (!indicatorDetail || !zoomImage || !zoomStage || !zoomDialog?.open) return;
      const width = zoomStage.clientWidth;
      const height = zoomStage.clientHeight;
      const nativeWidth = zoomImage.naturalWidth;
      const nativeHeight = zoomImage.naturalHeight;
      if (!(width && height && nativeWidth && nativeHeight)) return;
      const fit = Math.min(width / nativeWidth, height / nativeHeight);
      const focusX = (width - nativeWidth * fit) / 2 + nativeWidth * fit * indicatorDetail.x;
      const focusY = (height - nativeHeight * fit) / 2 + nativeHeight * fit * indicatorDetail.y;
      zoomImage.style.transformOrigin = '0 0';
      zoomImage.style.transform = `translate(${width / 2 - focusX * indicatorDetail.scale}px, ${height / 2 - focusY * indicatorDetail.scale}px) scale(${indicatorDetail.scale})`;
    }
    zoomImage?.addEventListener('load', applyIndicatorZoom);
    window.addEventListener('resize', applyIndicatorZoom, { passive: true });
    function resetZoomDetail() {
      indicatorDetail = null;
      zoomDialog?.classList.remove('is-indicator-detail');
      if (zoomImage) {
        zoomImage.style.removeProperty('transform');
        zoomImage.style.removeProperty('transform-origin');
      }
    }
    function setZoomDescription(text) {
      const description = zoomDialog && $('p', zoomDialog);
      if (description) description.textContent = text;
    }
    function updateZoom() {
      if (!product || indicatorDetail) return;
      const paths = galleryPaths();
      const node = $('#zoom-image');
      if (node) {
        node.src = base + paths[photoIndex];
        node.alt = `${product.name}, ${photoIndex === 0 ? colorsFor(product)[selectedColor].name : 'shared product detail'}, image ${photoIndex + 1} of ${paths.length}`;
      }
      setZoomDescription(galleryCaption());
      if ($('#zoom-count')) $('#zoom-count').textContent = `${photoIndex + 1} / ${paths.length}`;
      ['#zoom-prev', '#zoom-next'].forEach(selector => { if ($(selector)) $(selector).disabled = paths.length < 2; });
    }
    function renderGallery(rebuildThumbs = false) {
      if (!product) return;
      const paths = galleryPaths();
      photoIndex = Math.max(0, Math.min(paths.length - 1, photoIndex));
      const color = colorsFor(product)[selectedColor];
      if (galleryStage) galleryStage.innerHTML = art(paths[photoIndex], `${product.name}, ${photoIndex === 0 ? color.name : 'product detail'}, image ${photoIndex + 1}`, 'eager');
      if ($('#gallery-caption')) $('#gallery-caption').textContent = galleryCaption();
      const zoomButton = $('[data-zoom]');
      if (zoomButton) zoomButton.setAttribute('aria-label', `Enlarge image ${photoIndex + 1} of ${product.name}`);
      if ($('#gallery-count')) $('#gallery-count').textContent = `${String(photoIndex + 1).padStart(2, '0')} / ${String(paths.length).padStart(2, '0')}`;
      const thumbs = $('#gallery-thumbs');
      if (thumbs && rebuildThumbs) thumbs.innerHTML = paths.map((path, index) => `<button type="button" class="gallery-thumb${index === photoIndex ? ' is-active is-selected' : ''}" data-photo="${index}" aria-label="View image ${index + 1} of ${escape(product.name)}" aria-pressed="${index === photoIndex}"><span class="thumb-art product-art art-fit">${art(path, '')}</span></button>`).join('');
      $$('.gallery-thumb[data-photo]').forEach(button => {
        const selected = Number(button.dataset.photo) === photoIndex;
        button.classList.toggle('is-active', selected);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      ['#gallery-prev', '#gallery-next'].forEach(selector => { if ($(selector)) $(selector).disabled = paths.length < 2; });
      updateZoom();
    }
    function movePhoto(direction) {
      if (indicatorDetail) return;
      const paths = galleryPaths();
      if (!paths.length) return;
      photoIndex = (photoIndex + direction + paths.length) % paths.length;
      renderGallery();
    }
    function updateProductQuery() {
      if (!product) return;
      const query = new URLSearchParams(window.location.search);
      if (selectedColor) query.set('color', String(selectedColor)); else query.delete('color');
      if (selectedDevice !== devicesFor(product)[0]) query.set('device', selectedDevice); else query.delete('device');
      try { history.replaceState(null, '', window.location.pathname + (query.size ? `?${query}` : '') + window.location.hash); } catch (_) { /* Local viewer support varies. */ }
    }
    function selectColor(index, updateQuery = true) {
      if (!product || !Number.isInteger(index) || !colorsFor(product)[index]) return;
      selectedColor = index;
      photoIndex = 0;
      currentColors.set(product.id, index);
      if ($('#color-name')) $('#color-name').textContent = colorsFor(product)[index].name;
      $$('[data-color]').forEach(button => {
        const selected = Number(button.dataset.color) === index;
        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      renderGallery(true);
      updateCard(product, index);
      if (updateQuery) updateProductQuery();
    }
    function selectDevice(device, updateQuery = true) {
      if (!product || !devicesFor(product).includes(device)) return;
      selectedDevice = device;
      $$('[data-device]').forEach(button => {
        const selected = button.dataset.device === device;
        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      if (updateQuery) updateProductQuery();
    }
    function updatePdpQuantity(value) {
      const field = $('#product-quantity');
      const quantity = clampQuantity(value);
      if (field) field.value = String(quantity);
      $$('[data-pdp-qty]').forEach(button => { button.disabled = Number(button.dataset.pdpQty) < 0 ? quantity <= 1 : quantity >= 99; });
      return quantity;
    }
    function addToBag() {
      if (!product) return;
      const quantity = updatePdpQuantity($('#product-quantity')?.value);
      let added = 0;
      saveBag(latest => {
        const existing = latest.find(item => item.id === product.id && item.color === selectedColor && item.device === selectedDevice);
        added = Math.min(quantity, 99 - (existing?.qty || 0));
        if (existing) existing.qty += added;
        else if (added) latest.push({ id: product.id, color: selectedColor, device: selectedDevice, qty: added });
        return latest;
      });
      if (!added) { toast('This selection has reached the 99-item limit.', true); return; }
      toast(`${added} ${added === 1 ? 'item' : 'items'} added to your bag.${added < quantity ? ' Maximum quantity: 99.' : ''}`, true);
    }
    if (!window.XIKAVY_SHOPIFY) $('#add-to-bag')?.addEventListener('click', addToBag);
    $('#product-quantity')?.addEventListener('change', event => updatePdpQuantity(event.target.value));
    $('#product-quantity')?.addEventListener('blur', event => updatePdpQuantity(event.target.value));
    $('#gallery-prev')?.addEventListener('click', () => movePhoto(-1));
    $('#gallery-next')?.addEventListener('click', () => movePhoto(1));
    $('#zoom-prev')?.addEventListener('click', () => movePhoto(-1));
    $('#zoom-next')?.addEventListener('click', () => movePhoto(1));
    $('#zoom-close')?.addEventListener('click', () => zoomDialog?.close());
    if (zoomDialog) {
      zoomDialog.addEventListener('close', () => {
        document.documentElement.classList.remove('dialog-is-open');
        resetZoomDetail();
        if (zoomTrigger?.isConnected) zoomTrigger.focus({ preventScroll: true });
      });
      zoomDialog.addEventListener('click', event => {
        if (event.target !== zoomDialog) return;
        const box = zoomDialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) zoomDialog.close();
      });
      zoomDialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); movePhoto(event.key === 'ArrowLeft' ? -1 : 1); }
      });
    }
    if (product && page === 'product') {
      const commerceNode = document.getElementById('xikavy-commerce-data');
      if (window.XIKAVY_SHOPIFY && commerceNode && params.has('variant')) {
        const record = JSON.parse(commerceNode.textContent).products[product.id];
        const variant = record?.variants.find(item => String(item.id) === params.get('variant'));
        if (variant) { params.set('color', String(colorsFor(product).findIndex(color => color.name === variant.color))); params.set('device', variant.device); }
      }
      const queryColor = params.has('color') ? Number(params.get('color')) : 0;
      selectColor(Number.isInteger(queryColor) && colorsFor(product)[queryColor] ? queryColor : 0, false);
      selectDevice(resolveDevice(product, params.get('device')) || devicesFor(product)[0], false);
      updatePdpQuantity($('#product-quantity')?.value || 1);
    }

    function syncSearchFormState() {
      if (window.XIKAVY_SHOPIFY) return;
      const form = $('#search-form');
      if (!form) return;
      $$('input[data-selection-state]', form).forEach(input => input.remove());
      const name = window.location.protocol !== 'file:' && selectionStore.storageOK ? '' : bag.length ? 'selection' : explicitlyEmpty ? 'bagempty' : '';
      if (!name) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = bag.length ? JSON.stringify(bag) : '1';
      input.dataset.selectionState = '';
      form.appendChild(input);
    }
    function renderSearch(updateQuery = false) {
      const input = $('#search-input');
      const results = $('#search-results');
      if (!input || !results) return;
      const query = input.value.trim();
      const matches = searchMatches(products, query, currentColors);
      results.innerHTML = matches.length ? matches.map(match => {
        const item = match.product;
        const color = colorsFor(item)[match.color];
        return `<a class="search-result" href="${escape(selectionHref(productUrl(item, match.color, match.device)))}"><span class="search-result-art product-art art-fit">${art(color.image || item.image, `${item.name} in ${color.name}`)}</span><span class="search-result-copy"><strong>${escape(item.name)}</strong><span>${escape(seriesName(item))}</span><span class="search-match">${escape(color.name)}${match.device ? ` · ${escape(match.device)}` : ''}</span><span>${money(item.price)}</span></span>${arrow}</a>`;
      }).join('') : '<div class="search-empty"><h2>No designs found.</h2><p>Try a device, material, color, or product name.</p></div>';
      const count = $('#search-count') || $('[data-search-count]');
      if (count) count.textContent = `${matches.length} ${matches.length === 1 ? 'design' : 'designs'}${query ? ` for “${query}”` : ''}`;
      if (updateQuery) {
        const next = new URLSearchParams(window.location.search);
        if (query) next.set('q', query); else next.delete('q');
        try { history.replaceState(null, '', window.location.pathname + (next.size ? `?${next}` : '') + window.location.hash); } catch (_) { /* Search still works when history is unavailable. */ }
      }
    }
    if (page === 'search' && $('#search-input')) {
      $('#search-input').value = params.get('q') || '';
      $('#search-input').addEventListener('input', () => renderSearch(true));
      renderSearch();
    }
    syncSearchFormState();
    $('#search-form')?.addEventListener('submit', () => { syncBag(false); syncSearchFormState(); });

    const sort = $('#sort-products');
    const list = $('.collection-products');
    const originalCards = list ? $$('article[data-card]', list) : [];
    function sortProducts() {
      if (!list || !sort) return;
      const mode = sort.value;
      const cards = [...originalCards];
      if (/price|low|high/.test(mode)) cards.sort((a, b) => {
        const difference = (productById(a.dataset.card)?.price || 0) - (productById(b.dataset.card)?.price || 0);
        return /desc|high/.test(mode) ? -difference : difference;
      });
      else if (/name|alpha|a-z/.test(mode)) cards.sort((a, b) => (productById(a.dataset.card)?.name || '').localeCompare(productById(b.dataset.card)?.name || ''));
      cards.forEach(card => list.appendChild(card));
      const count = $('#collection-count') || $('[data-result-count]');
      if (count) count.textContent = `${cards.length} ${cards.length === 1 ? 'design' : 'designs'}`;
    }
    sort?.addEventListener('change', sortProducts);

    document.addEventListener('click', event => {
      const target = event.target.closest?.('button,a');
      if (!target) return;
      if (target.matches('[data-card-color]')) { const item = productById(target.dataset.productId); if (item) updateCard(item, Number(target.dataset.cardColor)); return; }
      if (target.matches('[data-color]')) { selectColor(Number(target.dataset.color)); return; }
      if (target.matches('[data-device]')) { selectDevice(target.dataset.device); return; }
      if (target.matches('.gallery-thumb[data-photo]')) { const index = Number(target.dataset.photo); if (Number.isInteger(index) && galleryPaths()[index]) { photoIndex = index; renderGallery(); } return; }
      if (target.matches('[data-pdp-qty]')) { updatePdpQuantity(clampQuantity($('#product-quantity')?.value) + Number(target.dataset.pdpQty)); return; }
      if (target.matches('[data-zoom]:not([data-indicator-detail])') && zoomDialog && product) {
        resetZoomDetail();
        zoomTrigger = target;
        updateZoom();
        if (!zoomDialog.open) zoomDialog.showModal();
        document.documentElement.classList.add('dialog-is-open');
        $('#zoom-close')?.focus();
        return;
      }
      if (target.matches('.sticky-add')) {
        // Reset inner positions left by an older layout or restored browser state.
        $$('.pdp-info,.purchase-scroll,#purchase').forEach(node => { node.scrollTop = 0; });
        const overview = $('#overview') || $('#purchase');
        const heading = $('#purchase-title') || $('#purchase h1');
        if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
        overview?.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
        return;
      }
      if (target.matches('[data-indicator-detail]') && zoomDialog && zoomImage) {
        zoomTrigger = target;
        const key = target.dataset.image || target.dataset.indicatorDetail;
        const source = assets[key]?.src || key;
        const rawX = Number(target.dataset.focusX);
        const rawY = Number(target.dataset.focusY);
        const x = Math.min(1, Math.max(0, Number.isFinite(rawX) ? rawX : 0.5));
        const y = Math.min(1, Math.max(0, Number.isFinite(rawY) ? rawY : 0.5));
        const scale = Math.min(12, Math.max(1, Number(target.dataset.zoom) || 4));
        indicatorDetail = { x, y, scale };
        zoomImage.src = base + source;
        zoomImage.alt = target.dataset.caption || target.getAttribute('aria-label') || 'Temperature indicator detail in the original product image';
        zoomDialog.classList.add('is-indicator-detail');
        ['#zoom-prev', '#zoom-next'].forEach(selector => { if ($(selector)) $(selector).disabled = true; });
        setZoomDescription('Temperature indicator · Enlarged detail from the original image.');
        if (!zoomDialog.open) zoomDialog.showModal();
        requestAnimationFrame(applyIndicatorZoom);
        document.documentElement.classList.add('dialog-is-open');
        $('#zoom-close')?.focus();
        return;
      }
      if (target.matches('[data-bag-qty]')) {
        const index = Number(target.dataset.bagQty);
        if (!bag[index]) return;
        const direction = Number(target.dataset.delta);
        const chosen = { ...bag[index] };
        saveBag(latest => {
          const current = latest.find(item => item.id === chosen.id && item.color === chosen.color && item.device === chosen.device);
          if (current) current.qty = clampQuantity(current.qty + direction);
          return latest;
        });
        const replacement = $(`[data-bag-qty="${index}"][data-delta="${direction}"]`);
        (replacement?.disabled ? $(`[data-bag-qty="${index}"]:not([disabled])`) : replacement)?.focus({ preventScroll: true });
        return;
      }
      if (target.matches('[data-bag-remove]')) {
        const index = Number(target.dataset.bagRemove);
        if (!bag[index]) return;
        const chosen = { ...bag[index] };
        saveBag(latest => latest.filter(item => !(item.id === chosen.id && item.color === chosen.color && item.device === chosen.device)));
        const replacement = $$('[data-bag-remove]')[Math.min(index, bag.length - 1)] || $('#bag-empty a');
        replacement?.focus({ preventScroll: true });
      }
    });

    // Native image loads update visual feedback without changing any source file.
    const imageStates = new WeakMap();
    const imageContainers = '.art-fit,.feature-media,.detail-artwork,.detail-media,.installation-graphic,.home-material-story figure,.gallery-main,.card-picture,.temperature-detail,.zoom-image-stage,.engineering-picture,.guide-step-image';
    function updateImageContainer(container) {
      const images = $$('img', container);
      if (!images.length) return;
      const loading = images.some(img => imageStates.get(img)?.status === 'loading');
      const error = !loading && images.some(img => imageStates.get(img)?.status === 'error');
      container.classList.add('image-state');
      container.classList.toggle('is-loading', loading);
      container.classList.toggle('is-error', error);
      container.classList.toggle('is-loaded', !loading && !error);
      container.setAttribute('aria-busy', String(loading));
      let message = Array.from(container.children).find(child => child.classList?.contains('image-error-message'));
      if (error && !message) {
        message = document.createElement('span');
        message.className = 'image-error-message';
        message.setAttribute('role', 'status');
        message.textContent = 'Image could not load. Refresh to try again.';
        container.appendChild(message);
      } else if (!error) message?.remove();
    }
    function watchImage(img, reset = false) {
      const container = img.closest(imageContainers);
      if (!container) return;
      const prior = imageStates.get(img);
      if (prior && !reset && prior.source === img.getAttribute('src')) return;
      const state = { source: img.getAttribute('src'), status: img.complete ? img.naturalWidth > 0 ? 'loaded' : 'error' : 'loading' };
      imageStates.set(img, state);
      if (!prior) {
        img.addEventListener('load', () => { const current = imageStates.get(img); if (current) current.status = 'loaded'; updateImageContainer(img.closest(imageContainers) || container); });
        img.addEventListener('error', () => { const current = imageStates.get(img); if (current) current.status = 'error'; updateImageContainer(img.closest(imageContainers) || container); });
      }
      updateImageContainer(container);
    }
    $$('img').forEach(img => watchImage(img));
    if ('MutationObserver' in window) new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.target instanceof HTMLImageElement) watchImage(mutation.target, true);
        for (const node of mutation.addedNodes || []) {
          if (!(node instanceof Element)) continue;
          if (node instanceof HTMLImageElement) watchImage(node);
          $$('img', node).forEach(img => watchImage(img));
        }
      }
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });

    const shopToggle = $('[data-shop-toggle]');
    const shopMenu = $('[data-shop-menu]') || $('#shop-menu');
    const shop = shopToggle?.closest('.nav-shop');
    function setShop(open, restoreFocus = false) {
      if (!shopToggle || !shopMenu || !shop) return;
      shop.classList.toggle('is-open', open);
      shopToggle.setAttribute('aria-expanded', String(open));
      shopMenu.setAttribute('aria-hidden', String(!open));
      shopMenu.inert = !open;
      if (restoreFocus) shopToggle.focus({ preventScroll: true });
    }
    shopToggle?.addEventListener('click', () => setShop(shopToggle.getAttribute('aria-expanded') !== 'true'));
    document.addEventListener('click', event => { if (shop && !shop.contains(event.target)) setShop(false); });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && shopToggle?.getAttribute('aria-expanded') === 'true') { event.preventDefault(); setShop(false, true); }
    });
    shop?.addEventListener('focusout', () => requestAnimationFrame(() => { if (!shop.contains(document.activeElement)) setShop(false); }));
    setShop(false);

    const sectionLinks = $$('[data-section-link],.product-tabbar a[href^="#"]');
    const watchedSections = sectionLinks.map(link => document.getElementById(link.hash.slice(1))).filter(Boolean);
    let sectionFrame = null;
    function updateCurrentSection() {
      sectionFrame = null;
      if (!watchedSections.length) return;
      const offset = ($('.site-header')?.getBoundingClientRect().height || 82) + ($('.product-tabbar')?.getBoundingClientRect().height || 56) + 40;
      let current = watchedSections[0];
      for (const section of watchedSections) if (section.getBoundingClientRect().top <= offset) current = section;
      sectionLinks.forEach(link => {
        const active = link.hash === `#${current.id}`;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'location'); else link.removeAttribute('aria-current');
      });
    }
    function queueSectionUpdate() { if (!sectionFrame) sectionFrame = requestAnimationFrame(updateCurrentSection); }
    window.addEventListener('scroll', queueSectionUpdate, { passive: true });
    window.addEventListener('resize', queueSectionUpdate, { passive: true });
    window.addEventListener('pageshow', queueSectionUpdate);
    updateCurrentSection();

    const hero = $('.hero');
    if (hero) {
      const header = $('.site-header');
      const updateHeader = () => header?.classList.toggle('is-scrolled', window.scrollY > 24);
      window.addEventListener('scroll', updateHeader, { passive: true });
      window.addEventListener('pageshow', updateHeader);
      updateHeader();
    }
    const slides = $$('.hero-slide');
    let heroIndex = 0;
    let paused = reducedMotion.matches;
    let visible = true;
    let heroLastChange = performance.now();
    function updatePause() {
      const button = $('#hero-pause');
      if (!button) return;
      button.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      button.setAttribute('aria-pressed', String(paused));
      button.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">${paused ? '<path d="m8 5 11 7-11 7Z"/>' : '<path d="M9 5v14M15 5v14"/>'}</svg>`;
    }
    function showSlide(index) {
      if (!slides.length) return;
      heroIndex = (index + slides.length) % slides.length;
      if (hero) hero.dataset.activeSlide = String(heroIndex);
      if (hero) $('.site-header')?.setAttribute('data-campaign', String(heroIndex));
      slides.forEach((slide, position) => {
        const active = position === heroIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      $$('[data-slide]').forEach(button => {
        const active = Number(button.dataset.slide) === heroIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      if ($('#hero-count')) $('#hero-count').textContent = `${String(heroIndex + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      heroLastChange = performance.now();
    }
    $('#hero-prev')?.addEventListener('click', () => showSlide(heroIndex - 1));
    $('#hero-next')?.addEventListener('click', () => showSlide(heroIndex + 1));
    $$('[data-slide]').forEach(button => button.addEventListener('click', () => showSlide(Number(button.dataset.slide))));
    $('#hero-pause')?.addEventListener('click', () => { paused = !paused; heroLastChange = performance.now(); updatePause(); });
    if (hero && slides.length) {
      if ('IntersectionObserver' in window) new IntersectionObserver(entries => { visible = entries[0].isIntersecting; }, { threshold: 0.1 }).observe(hero);
      hero.addEventListener('focusout', () => { heroLastChange = performance.now(); });
      hero.addEventListener('mouseleave', () => { heroLastChange = performance.now(); });
      reducedMotion.addEventListener?.('change', event => { if (event.matches) { paused = true; updatePause(); } });
      setInterval(() => {
        if (!paused && visible && !document.hidden && !hero.contains(document.activeElement) && !hero.matches(':hover') && performance.now() - heroLastChange >= 8000) showSlide(heroIndex + 1);
      }, 1000);
      document.addEventListener('visibilitychange', () => { heroLastChange = performance.now(); });
      showSlide(0);
      updatePause();
    }

    // One navigation tree serves desktop and touch screens.
    const menuButton = $('#menu-open');
    const menu = $('#mobile-menu');
    const compactScreen = matchMedia('(max-width: 1099px)');
    function setMenu(open, restoreFocus = false) {
      if (!menu || !menuButton) return;
      open = compactScreen.matches && open;
      menu.classList.toggle('is-open', open);
      menu.inert = compactScreen.matches && !open;
      if (compactScreen.matches) menu.setAttribute('aria-hidden', String(!open));
      else menu.removeAttribute('aria-hidden');
      menuButton.setAttribute('aria-expanded', String(open));
      menuButton.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      document.body.classList.toggle('mobile-nav-open', open);
      $('#main').inert = open;
      $('.site-footer').inert = open;
      if (!open) setShop(false);
      if (restoreFocus) menuButton.focus({preventScroll: true});
    }
    menuButton?.addEventListener('click', () => setMenu(menuButton.getAttribute('aria-expanded') !== 'true'));
    menu?.addEventListener('click', event => { if (event.target.closest('a[href]')) setMenu(false); });
    document.addEventListener('click', event => {
      if (menu?.classList.contains('is-open') && !menu.contains(event.target) && !menuButton.contains(event.target)) setMenu(false);
    });
    document.addEventListener('keydown', event => { if (event.key === 'Escape' && menu?.classList.contains('is-open')) setMenu(false, true); });
    compactScreen.addEventListener('change', () => setMenu(false));
    window.addEventListener('pageshow', () => setMenu(false));
    setMenu(false);

    function touchSwipe(node, move) {
      if (!node) return;
      let start = null, suppressClick = false;
      node.addEventListener('pointerdown', event => {
        suppressClick = false;
        if (event.pointerType === 'touch' && event.isPrimary) start = {x:event.clientX, y:event.clientY};
      }, {passive:true});
      node.addEventListener('pointercancel', () => { start = null; }, {passive:true});
      node.addEventListener('pointerup', event => {
        if (!start) return;
        const dx=event.clientX-start.x, dy=event.clientY-start.y; start=null;
        if (Math.abs(dx)>48 && Math.abs(dx)>Math.abs(dy)*1.5) {
          suppressClick=true; move(dx<0?1:-1);
          setTimeout(()=>{suppressClick=false;},350);
        }
      }, {passive:true});
      node.addEventListener('click', event=>{if(suppressClick){event.preventDefault();event.stopImmediatePropagation();suppressClick=false;}},true);
    }
    touchSwipe($('.gallery-main'), movePhoto);
    touchSwipe(hero, direction=>showSlide(heroIndex+direction));

    const rail = $('#home-product-rail');
    if (rail) {
      const updateRail = () => { const overflow = rail.scrollWidth - rail.clientWidth; $('.rail-line')?.style.setProperty('--progress', `${Math.min(100,(rail.scrollLeft + rail.clientWidth)/rail.scrollWidth*100)}%`); const prev=$('.rail-prev'),next=$('.rail-next'); if(prev)prev.disabled=rail.scrollLeft<2;if(next)next.disabled=overflow<2||rail.scrollLeft>=overflow-2; };
      $$('.rail-prev,.rail-next').forEach(button=>button.addEventListener('click',()=>rail.scrollBy({left:(button.matches('.rail-prev')?-1:1)*(rail.firstElementChild.getBoundingClientRect().width+20),behavior:scrollBehavior()})));
      rail.addEventListener('scroll',updateRail,{passive:true}); window.addEventListener('resize',updateRail,{passive:true}); updateRail();
    }
    updateBagCount();
    renderBag();
    stampLinks();
    sortProducts();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
